"use client";

import { useState } from "react";
import { useConfirm } from "@/app/components/ConfirmDialog";
import { Detail, JUDGMENT_GROUPS } from "./characterViews";

/** 耐性の 3 つの組。列の数は組ごと */
const RESISTANCE_GROUPS: { label: string; cols: number; keys: string[] }[] = [
  { label: "属性", cols: 9, keys: ["heat", "cold", "water", "electric", "holy", "dark", "blast", "magnetic", "dust"] },
  { label: "物理", cols: 9, keys: ["slash", "blunt", "pierce", "shot", "pressure", "bind", "tear", "twist", "vibration"] },
  { label: "特殊", cols: 7, keys: ["corrosion", "poison", "curse", "dry", "illusion", "sleep", "sound", "breath", "drain", "charm", "fear", "disease", "death", "mutation"] },
];

/** 1 を下回れば効きにくい、上回れば効きやすい。画面には 2 から引いた値を出す */
function weakClass(value: number): string {
  if (value < 1) return "pl-res-strong";
  if (value > 1) return "pl-res-weak";
  return "";
}

function Fold({ title, open, children }: { title: string; open?: boolean; children: React.ReactNode }) {
  return (
    <details className="pl-prof-sec pl-fold" open={open}>
      <summary>{title}</summary>
      <div className="pl-fold-body">{children}</div>
    </details>
  );
}

/**
 * 基礎ステータス。振り分けポイントが残っていれば、onSpend を持つときに ＋ と − で振れる。
 * 決めるまでは振る数を画面だけで持つ
 */
function BaseStatus({
  status,
  growth,
  onSpend,
}: {
  status: NonNullable<Detail["status"]>;
  growth: Detail["growth"];
  onSpend?: (spend: Record<string, number>) => Promise<string | null>;
}) {
  const [plan, setPlan] = useState<Record<string, number>>({});
  const [saving, setSaving] = useState(false);
  const [note, setNote] = useState("");
  const [ask, confirmDialog] = useConfirm();
  const points = growth?.points ?? 0;
  const used = Object.values(plan).reduce((n, v) => n + v, 0);
  const left = points - used;
  const editable = Boolean(onSpend) && points > 0;

  const bump = (key: string, delta: number) => {
    setNote("");
    setPlan((now) => {
      const next = Math.max(0, (now[key] ?? 0) + delta);
      if (delta > 0 && left <= 0) return now;
      return { ...now, [key]: next };
    });
  };
  const save = async () => {
    if (!onSpend || used === 0) return;
    const words = status.base.filter((row) => (plan[row.key] ?? 0) > 0).map((row) => `${row.label} +${plan[row.key]}`);
    if (!(await ask(`${words.join("・")}\nこの振り分けで決めます。決めた後は戻せません。`, { ok: "決める" }))) return;
    setSaving(true);
    const error = await onSpend(plan);
    setSaving(false);
    if (error) setNote(error);
    else {
      setPlan({});
      setNote("振り分けを決めました");
    }
  };

  return (
    <>
      {growth ? (
        <div className="pl-sk-head pl-grow-head">
          <p className="pl-grow-line">
            振り分けポイント <b>{left}</b>
            <small>
              成長値 {growth.total + used} / {growth.cap}
            </small>
          </p>
          {editable ? (
            <span className="pl-grow-actions">
              <button type="button" className="pl-tab" disabled={used === 0 || saving} onClick={() => setPlan({})}>
                戻す
              </button>
              <button type="button" className="pl-tab pl-tab-on" disabled={used === 0 || saving} onClick={save}>
                {saving ? "決めています" : "決める"}
              </button>
            </span>
          ) : null}
        </div>
      ) : null}
      {note ? <p className="pl-sk-note">{note}</p> : null}
      <div className="pl-base5">
        {status.base.map((row) => {
          const add = plan[row.key] ?? 0;
          return (
            <div key={row.key} className="pl-stat" style={{ "--pl-stat-img": `url("/Baunheim/ui/stat-${row.key}.webp")` } as React.CSSProperties}>
              <span>{row.label}</span>
              <b>
                {row.value}
                {add > 0 ? <em className="pl-grow-add">+{add}</em> : null}
              </b>
              {editable ? (
                <span className="pl-grow-step">
                  <button type="button" aria-label={`${row.label}を 1 減らす`} disabled={add === 0 || saving} onClick={() => bump(row.key, -1)}>
                    −
                  </button>
                  <button type="button" aria-label={`${row.label}を 1 増やす`} disabled={left <= 0 || saving} onClick={() => bump(row.key, 1)}>
                    ＋
                  </button>
                </span>
              ) : null}
            </div>
          );
        })}
      </div>
      {confirmDialog}
    </>
  );
}

/** ステータスのタブの、今の様子の下に畳んで並べる数値。所持金・基礎・判定・耐性と被弾 */
export default function PlayStatus({
  status,
  growth = null,
  onSpend,
}: {
  status: NonNullable<Detail["status"]>;
  growth?: Detail["growth"];
  /** 振り分けポイントを振るとき。失敗の文を返す。渡さないときは見るだけ */
  onSpend?: (spend: Record<string, number>) => Promise<string | null>;
}) {
  return (
    <div>
      <Fold title="所持金">
        <div className="review-grid">
          {status.money.map((one) => (
            <div className="review-cell" key={one.label}>
              <span>{one.label}</span>
              <strong>{one.amount}</strong>
            </div>
          ))}
        </div>
      </Fold>
      <Fold title={(growth?.points ?? 0) > 0 ? `基礎ステータス（振り分けポイント ${growth?.points}）` : "基礎ステータス"} open={Boolean(onSpend) && (growth?.points ?? 0) > 0}>
        <BaseStatus status={status} growth={growth} onSpend={onSpend} />
      </Fold>
      <Fold title="判定ステータス">
        {JUDGMENT_GROUPS.map((group) => (
          <div key={group.label} className="pl-judge-group">
            <p className="pl-judge-label">{group.label}</p>
            <div className="pl-base5 pl-grid8">
              {group.keys.map(([key, label]) => {
                const row = status.judgment.find((one) => one.key === key);
                return row ? (
                  <div key={key}>
                    <span>{label}</span>
                    <b>{row.value}</b>
                  </div>
                ) : null;
              })}
            </div>
          </div>
        ))}
      </Fold>
      <Fold title="耐性">
        {RESISTANCE_GROUPS.map((group) => (
          <div key={group.label} className="pl-judge-group">
            <p className="pl-judge-label">{group.label}</p>
            <div className="pl-base5 pl-res" style={{ gridTemplateColumns: `repeat(${group.cols}, minmax(0, 1fr))` }}>
              {group.keys.map((key) => {
                const row = status.resistance.find((one) => one.key === key);
                return row ? (
                  <div key={key}>
                    <span>{row.label}</span>
                    <b className={weakClass(row.value)}>{(2 - row.value).toFixed(1)}</b>
                  </div>
                ) : null;
              })}
            </div>
          </div>
        ))}
        <div className="pl-judge-group">
          <p className="pl-judge-label">被弾</p>
          <div className="pl-base5 pl-res" style={{ gridTemplateColumns: `repeat(${Math.max(1, status.damage_taken.length)}, minmax(0, 1fr))` }}>
            {status.damage_taken.map((row) => (
              <div key={row.key}>
                <span>{row.label.replace(/の被弾$/, "")}</span>
                <b className={weakClass(row.value)}>{(2 - row.value).toFixed(2)}</b>
              </div>
            ))}
          </div>
        </div>
      </Fold>
    </div>
  );
}
